import { useState, useEffect } from "react";
import { Stack, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Spinner, SpinnerSize, DefaultButton, Text } from "@fluentui/react";
import { getCosts } from "../../../api/admin/api_admin";
import { CostsResponse, CostLog, ServiceCost } from "../../../api/admin/models_admin";
import { AdminLabel, CellContent, ErrorDialog } from "../components";
import { ApiCostList } from "./apiCosts";

export const Costs = () => {
    const [costsData, setCostsData] = useState<CostsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);

    useEffect(() => {
        fetchCosts(currentYear, currentMonth);
    }, []);

    const fetchCosts = async (year: number, month: number) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getCosts(year, month);
            if ("error" in data) {
                setError(data.error);
            } else {
                setCostsData(data);
                setCurrentYear(data.target_year);
                setCurrentMonth(data.target_month);
            }
        } catch (err) {
            setError("コスト情報の取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handlePrevMonth = () => {
        let newYear = currentYear;
        let newMonth = currentMonth - 1;
        if (newMonth === 0) {
            newYear -= 1;
            newMonth = 12;
        }
        fetchCosts(newYear, newMonth);
    };

    const handleCurrentMonth = () => {
        const now = new Date();
        fetchCosts(now.getFullYear(), now.getMonth() + 1);
    };

    const handleNextMonth = () => {
        let newYear = currentYear;
        let newMonth = currentMonth + 1;
        if (newMonth === 13) {
            newYear += 1;
            newMonth = 1;
        }
        fetchCosts(newYear, newMonth);
    };

    const formatNumber = (num: number | null | undefined): string => {
        if (num === null || num === undefined) return '0';
        return num.toLocaleString('ja-JP');
    };

    const formatCost = (num: number | null | undefined): string => {
        if (num === null || num === undefined) return '不明';
        return num.toFixed(3);
    };

    const calculateTotal = (tokenCount: number | null, cost: number | null): string => {
        if (tokenCount === null || tokenCount === undefined || cost === null || cost === undefined) {
            return '不明';
        }
        return (tokenCount * cost).toFixed(2);
    };

    const costLogColumns: IColumn[] = [
        {
            key: 'model',
            name: 'モデル名',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (log: CostLog) => (
                <CellContent>{log.model || '不明'}</CellContent>
            )
        },
        {
            key: 'type',
            name: '入出力',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: (log: CostLog) => (
                <CellContent>{log.type === 1 ? '入力' : '出力'}</CellContent>
            )
        },
        {
            key: 'total_token_count',
            name: 'トークン数',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (log: CostLog) => (
                <div style={{ textAlign: 'right', fontSize: "11pt", fontWeight: 400, color: "black" }}>
                    {formatNumber(log.total_token_count)}
                </div>
            )
        },
        {
            key: 'unit_cost',
            name: '単価(円)\n(1,000 トークンあたり)',
            minWidth: 120,
            maxWidth: 180,
            isResizable: true,
            isMultiline: true,
            onRender: (log: CostLog) => (
                <div style={{ textAlign: 'right', fontSize: "11pt", fontWeight: 400, color: "black" }}>
                    {log.costs !== null && log.costs !== undefined ? formatCost(log.costs * 1000) : '不明'}
                </div>
            )
        },
        {
            key: 'total_cost',
            name: '金額(円)',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (log: CostLog) => (
                <div style={{ textAlign: 'right', fontSize: "11pt", fontWeight: 400, color: "black" }}>
                    {calculateTotal(log.total_token_count, log.costs)}
                </div>
            )
        }
    ];

    const serviceCostColumns: IColumn[] = [
        {
            key: 'service_name',
            name: 'サービス名',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (service: ServiceCost) => (
                <CellContent>{service.service_name || '不明'}</CellContent>
            )
        },
        {
            key: 'total_count',
            name: 'カウント',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (service: ServiceCost) => (
                <div style={{ textAlign: 'right', fontSize: "11pt", fontWeight: 400, color: "black" }}>
                    {service.total_count !== null && service.total_count !== undefined 
                        ? formatNumber(service.total_count) 
                        : ''}
                </div>
            )
        },
        {
            key: 'unit_name',
            name: '単位',
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            onRender: (service: ServiceCost) => (
                <CellContent>{service.unit_name || ''}</CellContent>
            )
        },
        {
            key: 'total_cost',
            name: '金額(円)',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (service: ServiceCost) => (
                <div style={{ textAlign: 'right', fontSize: "11pt", fontWeight: 400, color: "black" }}>
                    {service.total_cost !== null && service.total_cost !== undefined 
                        ? formatCost(service.total_cost) 
                        : '不明'}
                </div>
            )
        }
    ];

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="コスト照会" />
            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="end">
                <DefaultButton text="前の月" onClick={handlePrevMonth} />
                <DefaultButton text="今月" onClick={handleCurrentMonth} />
                <DefaultButton text="次の月" onClick={handleNextMonth} />
            </Stack>

            <Text variant="xLarge" block>
                {costsData?.target_year}年{costsData?.target_month}月分 : 約 {costsData?.costs_total} 円 {costsData?.msg}
            </Text>

            <Stack tokens={{ childrenGap: 20 }}>
                <DetailsList
                    items={costsData?.loglist || []}
                    columns={costLogColumns}
                    layoutMode={DetailsListLayoutMode.justified}
                    selectionMode={SelectionMode.none}
                    isHeaderVisible={true}
                />

                <DetailsList
                    items={costsData?.service_costs || []}
                    columns={serviceCostColumns}
                    layoutMode={DetailsListLayoutMode.justified}
                    selectionMode={SelectionMode.none}
                    isHeaderVisible={true}
                />

                <ApiCostList apiCosts={costsData?.api_costs} />
                <Text>
                    ※小数第一位を四捨五入のうえ、ご請求させていただきます
                </Text>
            </Stack>
        </Stack>
    );
}